

import { CdkListbox, CdkListboxModule } from '@angular/cdk/listbox';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, EventEmitter, HostListener, OnInit, Output, ViewChild, computed, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, switchMap, tap } from 'rxjs/operators';
import { OverlayLoginComponent } from 'src/app/authentication/components/overlay-login/overlay-login.component';
import { HeaderData, SearchSuggestionConfig, SearchSuggestionConfiguration, SearchSuggestionItem } from 'src/app/components/dynamic-header/category/header-data';
import { DeviceType } from 'src/app/components/dynamic-header/dynamic-header.component';
import { SearchCartQuantityInputComponent } from 'src/app/components/search-cart-quantity-input/search-cart-quantity-input.component';
import { AutoFocusDirective } from 'src/app/components/search-suggestion/directive/auto-focus.directive';
import { TextHighlightDirective } from 'src/app/components/search-suggestion/directive/text-highlight.directive';
import { OUT_OF_STOCK } from 'src/app/core/constants/error-messages';
import { IMAGE_URLS } from 'src/app/core/constants/image-source';
import { SUGGESTION_KEY } from 'src/app/core/enums/suggestion-search-key.enum';
import { ServerError } from 'src/app/core/models/server-error.model';
import { UserSession } from 'src/app/core/models/user.model';
import { AdsManagementService } from 'src/app/core/services/adsManagement/ads-management.service';
import { AddToCart, CartItem, UpdateCart } from 'src/app/core/services/cart/cart-api.service';
import { CartRepositoryService } from 'src/app/core/services/cart/cart-repository.service';
import { ConversionTrackingService } from 'src/app/core/services/conversion/conversion-tracking.service';
import { NotificationService } from 'src/app/core/services/notification/notification.service';
import { MyOverlayRef } from 'src/app/core/services/overlay/myoverlay-ref';
import { OverlayService } from 'src/app/core/services/overlay/overlay.service';
import { PendingCartService } from 'src/app/core/services/pending-cart/pending-cart.service';
import { PlatformService } from 'src/app/core/services/platform/platform.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { Product } from 'src/app/products/models/product.model';
import { IndianCurrencyPipe } from 'src/app/shared/pipes/indian-currency.pipe';
import { PercentageDiscountPipe } from 'src/app/shared/pipes/percentage-discount.pipe';
import { SearchSuggestionRepositoryService } from '../../service/search-suggestion-repository.service';
export interface Suggestion { suggestionValue: string, suggestionKey: string };

// Mapping between response keys and configuration properties
const KEY_CONFIG_MAPPING: { [key: string]: keyof SearchSuggestionConfig } = {
  'category_name_keywords': 'show_category_search_keywords',
  'display_name_keywords': 'show_display_name_search_keywords',
  'seo_detail_keywords': 'show_seo_detail_search_keywords',
  'products': 'show_product_search'
};

const DEFAULT_SEARCH_SUGGESTION_CONFIGURATION: SearchSuggestionConfiguration = {
  desktop_configuration: {
    show_product_search: true,
    show_search_keywords: true,
    show_category_search_keywords: true,
    show_display_name_search_keywords: true,
    show_seo_detail_search_keywords: true
  },
  mobile_configuration: {
    show_product_search: true,
    show_search_keywords: true,
    show_category_search_keywords: true,
    show_display_name_search_keywords: true,
    show_seo_detail_search_keywords: true
  }
};


@Component({
  selector: 'base-search-bar',
  templateUrl: './search-bar.component.html',
  styleUrls: ['./search-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    CdkListboxModule,
    SearchCartQuantityInputComponent,
    TextHighlightDirective,
    AutoFocusDirective,
    IndianCurrencyPipe,
    PercentageDiscountPipe
  ],
  standalone: true
})
export class SearchBarComponent implements OnInit {

  // Input signals for Angular 20
  cartItem = input<CartItem>();
  selectedDeviceType = input<string>("");
  headerData = input<HeaderData>();
  searchInputControl = input<FormControl>();
  isFocused = input<boolean>(false);
  searchSuggestionConfiguration = input<SearchSuggestionConfiguration | undefined>(undefined);

  // ViewChild references
  @ViewChild('searchBarContainer') searchBarContainer!: ElementRef;
  @ViewChild(CdkListbox) cdkListbox!: CdkListbox;

  @Output() searchProduct$ = new EventEmitter();
  @Output() searchIconClick$ = new EventEmitter();

  // Signals for reactive state
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  user = signal<UserSession | null>(null);
  isAuthPage = signal<boolean>(false);
  error$ = new Subject();
  IMAGE_URLS = IMAGE_URLS;
  isShowSuggestion = signal<boolean>(false);

  keyValue = signal<string>('');
  isLoggedIn = signal<boolean>(false);
  SUGGESTION_KEY = SUGGESTION_KEY;

  suggestions$!: Observable<any>;

  isSuggestionKeyNav = signal<boolean>(false);
  isBrowser = signal<boolean>(false);
  errorMessage = signal<string>('');
  deviceType = DeviceType;
  productsResponse = signal<any[]>([]);
  loginRef!: MyOverlayRef<OverlayLoginComponent>;
  _sellingPrice!: number;
  _percentSavings!: number;
  _actualPrice!: number;

  // Computed signals for derived state
  searchSuggestionConfigurationBasedOnDevice = computed<SearchSuggestionConfig>(() => {
    const deviceType = this.selectedDeviceType();
    const inputConfig = this.searchSuggestionConfiguration();
    const headerDataConfig = this.headerData()?.searchSuggestionConfiguration;
    const config = headerDataConfig || inputConfig || DEFAULT_SEARCH_SUGGESTION_CONFIGURATION;

    return deviceType === this.deviceType.desktop
      ? config?.desktop_configuration || DEFAULT_SEARCH_SUGGESTION_CONFIGURATION.desktop_configuration
      : config?.mobile_configuration || DEFAULT_SEARCH_SUGGESTION_CONFIGURATION.mobile_configuration;
  });

  shouldEnableSuggestions = computed<boolean>(() => {
    const config = this.searchSuggestionConfigurationBasedOnDevice();
    return (config?.show_search_keywords || config?.show_product_search) ?? true;
  });

  private destroyRef = inject(DestroyRef);
  public router = inject(Router);
  private userService = inject(UserService);
  public overlayService = inject(OverlayService);
  private platformService = inject(PlatformService);
  public cartRepositoryService = inject(CartRepositoryService);
  private notificationService = inject(NotificationService);
  private searchSuggestionService = inject(SearchSuggestionRepositoryService);
  private pendingCartService = inject(PendingCartService);
  private conversionTrackingService = inject(ConversionTrackingService);
  private adsManagementService = inject(AdsManagementService);

  constructor() {
    this.isBrowser.set(this.platformService.isBrowser());

    // Effect for user changes
    effect(() => {
      const currentUser = this.user();
      this.isLoggedIn.set(!!currentUser);
    });

    // Effect for auth page detection
    effect(() => {
      const currentUrl = this.router.url;
      this.isAuthPage.set(currentUrl.includes('auth'));
    });

    // Effect for cart changes - triggers change detection when cart updates
    effect(() => {
      const cart = this.cartRepositoryService.cart();
    });

  }

  ngOnInit(): void {
    this.user.set(this.userService.appUser);
    this.setupSuggestionsObservable();
  }

  private setupSuggestionsObservable(): void {
    const searchControl = this.searchInputControl();

    if (searchControl) {
      this.suggestions$ = searchControl.valueChanges.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        filter((inputValue) => this.keyValue() !== ' ' || !inputValue),
        filter(() => this.shouldEnableSuggestions()),
        switchMap(async inputValue => {
          inputValue = inputValue && inputValue.trim();
          if (inputValue) {
            this.isShowSuggestion.set(true);

            await this.searchSuggestionService.getSearchSuggestion(inputValue);
            const response = this.searchSuggestionService.searchSuggestions();

            if (response) {
              const config = this.searchSuggestionConfigurationBasedOnDevice();
              const { show_product_search = true, show_search_keywords = true } = config || {};
              const { products: productsResponse, ...keywordResponse } = response;

              const parsedKeywordResponse = [];

              if (show_search_keywords) {
                for (const key in keywordResponse) {
                  if (Array.isArray((keywordResponse as any)[key])) {
                    const configKey = KEY_CONFIG_MAPPING[key];
                    const shouldInclude = configKey ? config?.[configKey] : false;

                    if (shouldInclude) {
                      const keywords = (keywordResponse as any)[key]
                        .filter((item: any) => item !== "")
                        .map((suggestValue: any) => ({
                          suggestionValue: suggestValue,
                          suggestionKey: (this.SUGGESTION_KEY as any)[key]
                        }));
                      parsedKeywordResponse.push(...keywords);
                    }
                  }
                }
              }
              return { keywords: parsedKeywordResponse, products: show_product_search ? productsResponse : [] };
            }
            return { keywords: [], products: [] };
          } else {
            this.isShowSuggestion.set(false);
            return { keywords: [], products: [] };
          }
        }),
        tap((suggestion: any) => {
          if (suggestion?.keywords?.length) {
            this.cdkListbox._setActiveOption({ ...suggestion?.keywords?.[0] });
          } else {
            this.cdkListbox._setActiveOption({ ...suggestion?.products?.[0] });
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      );
    }
  }

  searchProduct(keyboardEvent: KeyboardEvent | Event | null, suggestionItem: SearchSuggestionItem | null) {
    this.searchProduct$.emit({ keyboardEvent, suggestionItem });
    if (keyboardEvent && 'key' in keyboardEvent) {
      this.keyValue.set(keyboardEvent.key);
      // Close suggestion menu when Enter is pressed
      if (keyboardEvent.key === 'Enter') {
        this.isShowSuggestion.set(false);
      }
    }
    // Close suggestion menu when a suggestion item is clicked
    if (suggestionItem) {
      this.isShowSuggestion.set(false);
    }
    this.isAuthPage.set(this.router.url.includes('auth'));
    this.user.set(this.userService.appUser);
  }

  inputClickUpdate() {
    this.isAuthPage.set(this.router.url.includes('auth'));
    this.user.set(this.userService.appUser);
  }

  inputClick() {
    this.searchIconClick$.emit()

  }

  compareSuggestion(suggestion1: Suggestion, suggestion2: Suggestion) {
    return suggestion1 === suggestion2;
  }

  @HostListener('document:click', ['$event.target'])
  private onSearchbarClick(targetElement: HTMLElement): void {
    // Check if the clicked element is inside the container
    const clickedInside = this.loginRef?.overlay?.overlayElement?.childNodes?.length
      ? true
      : this.searchBarContainer.nativeElement.contains(targetElement);

    if (!clickedInside) {
      // Click is outside, trigger your logic or update flags
      this.isShowSuggestion.set(false);
    }
  }

  searchCart(product: Product) {
    const currentUser = this.user();
    if (!currentUser?.access_token) {
      // Store product information for auto-add after login
      this.pendingCartService.storePendingProduct({
        id: product.id,
        bid_price: 0,
        quantity: 1,
        productDetailOverlay: false,
        stayOnCurrentPage: true
      });

      // Navigate to auth with return URL
      const currentUrl = this.router.url;
      this.router.navigate(['/auth/login-otp'], {
        queryParams: { returnUrl: currentUrl }
      });

      this.notificationService.showInfo('Please log in to your account to continue.');
      return;
    } else {
      this._addToCart(product.id, 0, 1);
    }

    // this._addToCart(product.id, 0, 1);
  }

  private async _addToCart(id: string, bid_price = 0, quantity = 1) {
    try {
      const collectedUtmAndCodeDetails = this.adsManagementService.getCollectedUtmAndCodeDetails();
      const cart: AddToCart = {
        cart_items: [
          {
            product: id,
            quantity: quantity,
            ...(bid_price && bid_price > 0 && {
              bid_price
            }),
          }
        ],
        ...(collectedUtmAndCodeDetails && Object.keys(collectedUtmAndCodeDetails)?.length && {
          attribution_data: {
            ...collectedUtmAndCodeDetails
          }
        })
      };

      await this.cartRepositoryService.addProductToCart(cart);
      // Get the updated cart for conversion tracking
      const updatedCart = this.cartRepositoryService.cart();
      if (updatedCart) {
        this.conversionTrackingService.trackAddToCartConversion(updatedCart);
      }
      this.notificationService.showSuccess('Product added to cart successfully.');
    } catch (error) {
      if (error instanceof HttpErrorResponse) {
        if (error?.error?.code === '1103') {
          this.errorMessage.set('Out of Stock');
        }
        const errorDetail: ServerError = error?.error;
        this.notificationService.showWarning(`${OUT_OF_STOCK}`);
      }
    }
  }

  navigateToProductDetailPage(route: string) {
    const url = this.router.serializeUrl(this.router.createUrlTree([route]));
    window.open(url, '_blank');
  }

  increaseQuantity(quantity: number, cartItemId: string) {
    this.updateQuantity(quantity, cartItemId)
  }

  decreaseQuantity(quantity: number, cartItemId: string) {
    if (quantity === 0) {
      this.removeCartItem(cartItemId);
    } else {
      this.updateQuantity(quantity, cartItemId)
    }
  }

  async removeCartItem(cartItemId: string) {
    try {
      await this.cartRepositoryService.removeProductFromCart(cartItemId);
    } catch (error) {
      // Handle error if needed
    }
  }

  async updateQuantity(quantity: number, cartItemId: string) {
    try {
      const reqObj: UpdateCart = {
        cart_items: [{
          id: cartItemId,
          quantity
        }]
      };
      await this.cartRepositoryService.updateCart(reqObj);
    } catch (error) {
      if (error instanceof HttpErrorResponse) {
        const errorDetail: ServerError = error?.error;
        this.error$.next(errorDetail?.message);
        const message = errorDetail?.detail?.[0]?.error;
        if (typeof message === 'string') {
          this.notificationService.showError(`${message}`);
        }
      }
    }
  }

  isProductInCart(productId: string, cart: any): boolean {
    return cart?.cart_items?.some((cartItem: any) => cartItem.product.id === productId) ?? false;
  }

  private checkAndHandlePendingCartProduct() {
    const currentUser = this.user();
    if (currentUser) {
      const pendingProduct = this.pendingCartService.getPendingProduct();
      if (pendingProduct) {
        this._addToCart(pendingProduct.id, pendingProduct.bid_price || 0, pendingProduct.quantity || 1);
        this.pendingCartService.clearPendingProduct();
      }
    }
  }

  // The destroyRef automatically handles cleanup

}
